import type { HTMLAttributes } from 'react'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

interface Props extends HTMLAttributes<HTMLButtonElement> {
  airport?: App.Airport
  offer?: App.Offer
  activeFilter?: string
  offerType: App.LEContractedAnalyticsType
  inSidebar?: boolean
  iconDisabled?: boolean
  ref?: React.Ref<HTMLButtonElement>
  charterBundleRoute?: App.CharterBundleRoute
  disabled?: boolean
}

function OfferAirportSelectBox(props: Props) {
  const {
    airport,
    activeFilter,
    offerType,
    offer,
    iconDisabled,
    charterBundleRoute,
    disabled: propDisabled,
    ...rest
  } = props

  const dispatch = useOfferPageDispatch()
  const isOpen = activeFilter === 'airport'
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const intl = useIntl()

  const onBoxClick = useCallback(() => {
    if (propDisabled || (isAgentHubEnabled && charterBundleRoute)) {
      // Prevent interaction when disabled or when agentHub is enabled for charter flights
      return
    }
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('AIRPORT_FILTER_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
      trackEvent('AIRPORT_FILTER_PRESSED')
    }
  }, [isOpen, dispatch, trackEvent, charterBundleRoute, propDisabled])

  const isAirportRequired = !!(offer?.bundledWithFlightsOnly || charterBundleRoute)
  const isDisabled = propDisabled || (isAgentHubEnabled && !!charterBundleRoute)

  const label = isAirportRequired
    ? intl.formatMessage({
        id: 'cBm_XCgZqQaeGy4UcUcz',
        defaultMessage: 'Departure airport',
        description: 'Label for departure airport filter when airport is required',
      })
    : intl.formatMessage({
        id: 'eHTEw-wwTZ-cRf0vNHIL',
        defaultMessage: 'Departure airport (optional)',
        description: 'Label for departure airport filter when airport is optional',
      })

  const placeholder = intl.formatMessage({
    id: 'c5WMYxsn0Kx-ArNEsZYc',
    defaultMessage: 'Select by airport',
    description: 'Placeholder text for airport selection dropdown on the offer detail page',
  })

  const airportValue = airport
    ? <b>{intl.formatMessage(
        {
          id: '2va0c7yMp42HxRSFN6GC',
          defaultMessage: '{airportName} ({airportCode})',
          description: 'Airport name with code in airport filter select box',
        },
        { airportName: airport.name, airportCode: airport.code.toUpperCase() },
      )}
    </b>
    : undefined

  return (<OfferFilterDropdownBox
    {...rest}
    data-testid="origin-select"
    onClick={isDisabled ? undefined : onBoxClick}
    active={isOpen}
    icon={!iconDisabled && <LinePlaneDepartureIcon />}
    label={label}
    value={airportValue}
    placeholder={placeholder}
  />
  )
}

OfferAirportSelectBox.displayName = 'OfferAirportSelectBox'

export default withOfferPageState((state) => ({
  airport: state.airport,
  activeFilter: state.activeFilter,
  offer: state.offer,
  offerType: state.offer?.analyticsType || state.bedbankOffer?.analyticsType,
  charterBundleRoute: state.charterBundleRoute,
}), OfferAirportSelectBox)
